/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import InformationButton from "../../../components/molecules/InformationButton"
import { TENANTS } from "../../../constants"
import { getCountryFromLanguage } from "../../../core/service/BusinessLogicService"

/* Utils */
import { isStrangerTenant, loc } from "../../../core/utils"
import { getSessionVoyageurLang } from "../../../core/voyageurLanguage"
import Date from "../../../form/Date"
import NumberField from "../../../form/NumberField"
import NumberFieldPhone from "../../../form/NumberFieldPhone"
import PhoneField from "../../../form/PhoneField"
import Select from "../../../form/Select"

/* Forms controls & validation */
import TextField from "../../../form/TextField"
import { DATE_FORMAT } from "../constants/ClientPageConstants"
import Bloc from "./Bloc"
import React from "react"
import { Col, FormGroup, Row } from "react-bootstrap"

export default function CoordinatesBloc(props) {
  const { tunnelData, country, values, blocFields, completedFields } = props
  const { tenant } = tunnelData
  const { clientPageConfiguration } = tunnelData
  const orderLanguageTag = tunnelData?.data?.order?.orderLanguageTag
  const countryCodeFromLanguage = getCountryFromLanguage(orderLanguageTag)
  const languageTag = orderLanguageTag || ""
  const isMediaMarkt = tenant === TENANTS.MEDIAMARKT_NL || tenant === TENANTS.MEDIAMARKT_BE
  const selectedLanguageTag = getSessionVoyageurLang() || languageTag
  const useMediaMarktDutchLabels = isMediaMarkt && selectedLanguageTag.startsWith("nl")
  let { coordinates, leaderCoordinates } = clientPageConfiguration

  coordinates = coordinates ? coordinates : leaderCoordinates
  const countedBlocFields = blocFields.filter((fieldClientPage) => !fieldClientPage.endsWith("additionalAddress"))

  const showIban = (Array.isArray(coordinates) && coordinates.includes("iban")) || coordinates?.iban != null
  const showBic = (Array.isArray(coordinates) && coordinates.includes("bic")) || coordinates?.bic != null

  return (
    <Bloc
      hide={!coordinates}
      name={leaderCoordinates ? "Leader coordinates" : "My coordinates"}
      completedFields={completedFields}
      totalFieldsNumber={countedBlocFields.length}
    >
      <div className="hard-shadow-white-block">
        {coordinates?.includes("address") && (
          <FormGroup>
            <Row noGutters>
              <Col>
                <TextField
                  noFormGroup
                  name="coordinates.address"
                  label={loc(useMediaMarktDutchLabels ? "Street name and house number" : "Address")}
                  trimOnBlur={true}
                  disabled={
                    tunnelData.data.order.simplifiedJourneyEnabled &&
                    !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.address?.editable
                  }
                >
                  <InformationButton overlayText={loc("Enter the address that will appear on your contract.")} />
                </TextField>
              </Col>
            </Row>
          </FormGroup>
        )}
        <Row>
          <Col lg={6}>
            <TextField
              hidden={!coordinates?.includes("additionalAddress")}
              name="coordinates.additionalAddress"
              label={loc`Address 2`}
              trimOnBlur={true}
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.additionalAddress?.editable
              }
            />
          </Col>
          <Col lg={6}>
            <TextField
              hidden={!coordinates?.includes("locality")}
              name="coordinates.locality"
              label={loc`Locality`}
              trimOnBlur={true}
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.locality?.editable
              }
            />
          </Col>
          <Col lg={4}>
            <TextField
              hidden={!coordinates?.includes("postalCode")}
              name="coordinates.postalCode"
              label={loc`Zip`}
              inputmode="numeric"
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.postalCode.editable
              }
            />
          </Col>
          <Col lg={8}>
            <Select
              hidden={!coordinates?.includes("city")}
              name="coordinates.city"
              label={loc`City`}
              disabled={
                (tunnelData.data.order.simplifiedJourneyEnabled &&
                  !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.city.editable) ||
                !values.coordinates.postalCode
              }
              url={
                values.coordinates.postalCode &&
                tunnelData.apiUrl +
                  "middle-business-service/api/v2/repository/cities/" +
                  countryCodeFromLanguage +
                  "/" +
                  values.coordinates.postalCode.replaceAll(" ", "") +
                  `?languageTag=${encodeURIComponent(languageTag)}`
              }
              token={tunnelData.access_token}
              enableMatchLabel={true}
              oneElement={true}
            />
          </Col>
          <Col lg={12}>
            <Select
              hidden={!coordinates?.includes("countryOfResidence")}
              name="coordinates.countryOfResidence"
              label={loc`Country of residence`}
              placeholder={loc`Select...`}
              url={
                tunnelData.apiUrl +
                "middle-business-service/api/v2/repository/countries/codes/" +
                countryCodeFromLanguage +
                `?languageTag=${encodeURIComponent(languageTag)}`
              }
              token={tunnelData.access_token}
            />
          </Col>
          <Col lg={12}>
            <Select
              name="coordinates.province"
              hidden={!coordinates?.includes("province")}
              label={loc`Province`}
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.province.editable
              }
              placeholder={loc`Select...`}
              url={
                tunnelData.apiUrl +
                "middle-business-service/api/v2/repository/departments/codes?journeyCountryCode=" +
                countryCodeFromLanguage +
                "&countryCode=" +
                countryCodeFromLanguage +
                (languageTag ? "&languageTag=" + encodeURIComponent(languageTag) : "")
              }
              token={tunnelData.access_token}
              enableMatchLabel={true}
            />
          </Col>
          <Col lg={12}>
            {isStrangerTenant(tenant) ? (
              <NumberFieldPhone
                countryCode={countryCodeFromLanguage}
                hidden={!coordinates?.includes("phoneNumber")}
                name="coordinates.phoneNumber"
                label={loc`Mobile`}
                trimOnBlur={true}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.phoneNumber?.editable
                }
              />
            ) : (
              <FormGroup>
                <PhoneField
                  countryCode={countryCodeFromLanguage}
                  hidden={!coordinates?.includes("phoneNumber")}
                  name="coordinates.phoneNumber"
                  label={loc`Mobile`}
                  disabled={
                    tunnelData.data.order.simplifiedJourneyEnabled &&
                    !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.phoneNumber?.editable
                  }
                />
              </FormGroup>
            )}
          </Col>
          <Col lg={12}>
            <NumberField
              hidden={!coordinates?.includes("fixePhoneNumber")}
              name="coordinates.fixePhoneNumber"
              label={loc`Landline`}
              trimOnBlur={true}
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.fixePhoneNumber?.editable
              }
            />
          </Col>
          <Col lg={12}>
            <TextField
              hidden={!coordinates?.includes("email")}
              name="coordinates.email"
              label={loc`Email`}
              trimOnBlur={true}
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.email?.editable
              }
            />
          </Col>
          <Col lg={8}>
            <TextField
              hidden={!showIban}
              name="coordinates.iban"
              label={loc`IBAN`}
              trimOnBlur={true}
              addIbanSeparator={true}
              formatOnBlur={true}
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.iban?.editable
              }
            />
          </Col>
          <Col lg={4}>
            <TextField
              hidden={!showBic}
              name="coordinates.bic"
              label={loc`BIC or SWIFT`}
              trimOnBlur={true}
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.bic?.editable
              }
            />
          </Col>
          <Col lg={8}>
            <Date
              hidden={!coordinates?.includes("yearBankSeniority")}
              name="coordinates.yearBankSeniority"
              label={loc`Bank Seniority`}
              format={DATE_FORMAT.coordinates.yearBankSeniority[country]}
              disabled={
                tunnelData.data.order.simplifiedJourneyEnabled &&
                !tunnelData?.simplifiedJourneyInputConfiguration?.coordinates?.yearBankSeniority?.editable
              }
            />
          </Col>
        </Row>
      </div>
    </Bloc>
  )
}
